import { FC, useState, useRef, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@contexts/AuthContext.tsx";
import {
  HarrisLogo,
  ChartBarIcon as DashboardIcon,
  SurveyIcon as SurveysIcon,
  DatasetsIcon,
  HelpIcon,
  SettingsIcon,
  AccountIcon,
  LogoutIcon,
} from "@assets/images";
import IconButton from "@mui/material/IconButton";
import { t } from "@utils/i18n";
import { useRoleAccess } from "@/hooks/use-role-access";

const Navigation: FC = () => {
  const { user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { canAccessRoute } = useRoleAccess();

  // Check if user can access different sections
  const canAccessSurveys = canAccessRoute("/surveys");
  const canAccessDatasets = canAccessRoute("/datasets");

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      if (
        menuRef.current &&
        event.target &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!user) {
    return null;
  }

  const navlinkClassNames = ({ isActive }: { isActive: boolean }) => {
    return `p-1.5 rounded-md ${isActive ? "bg-[#B75D69]" : ""} [&_svg_path]:!stroke-white [&_svg]:fill-none`;
  };

  const handleProfileClick = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const handleLogout = () => {
    setIsMenuOpen(false);
    logout();
  };

  // Get user initials from name
  const getUserInitials = (name?: string): string => {
    if (!name) return "U";

    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }

    const firstInitial = nameParts[0].charAt(0).toUpperCase();
    const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    return `${firstInitial}${lastInitial}`;
  };

  const userInitials = getUserInitials(user?.name);

  return (
    <nav className="w-20 sticky top-0 py-10 h-screen flex flex-col justify-between items-center bg-[#022B3A] border-r border-[#022B3A] z-50">
      <div className="flex flex-col items-center gap-6">
        <HarrisLogo />

        {canAccessSurveys && (
          <NavLink to="/surveys" className={navlinkClassNames}>
            <SurveysIcon />
          </NavLink>
        )}
        {canAccessDatasets && (
          <NavLink to="/datasets" className={navlinkClassNames}>
            <DatasetsIcon />
          </NavLink>
        )}
        <NavLink to="/dashboard" className={navlinkClassNames}>
          <DashboardIcon />
        </NavLink>
      </div>
      <div className="flex flex-col items-center gap-6">
        {/* {canAccessSupport && (
          <NavLink to="/support" className={navlinkClassNames}>
            <HelpIcon />
          </NavLink>
        )} */}
        {/* {canAccessSettings && (
          <NavLink to="/settings" className={navlinkClassNames}>
            <SettingsIcon />
          </NavLink>
        )} */}
        <div className="relative" ref={menuRef}>
          <IconButton
            onClick={handleProfileClick}
            aria-label={t("ariaLabels.openProfileMenu")}
          >
            <div className="rounded-full w-12 h-12 bg-[#F2F4F7] flex items-center justify-center text-[#667085] font-semibold text-lg">
              {userInitials}
            </div>
          </IconButton>

          {/* Profile Menu */}
          {isMenuOpen && (
            <div
              className="absolute bottom-full left-0 mb-2 w-48 bg-white border border-hp-border-secondary rounded-lg shadow-lg py-2 z-50"
              role="menu"
              aria-labelledby="profile-menu-button"
            >
              {/* <button
                onClick={handleAccountClick}
                className="w-full px-4 py-2 text-left text-sm text-hp-text-day-primary hover:bg-gray-50 flex items-center gap-3"
                role="menuitem"
              >
                <AccountIcon className="w-4 h-4" />
                {t("common.account")}
              </button> */}
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center gap-3"
              >
                <LogoutIcon className="w-4 h-4" />
                {t("common.logout")}
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
